// Platform-admin tenant administration API client (E1, US-1.6): create/list the tenant
// isolation root with the PLATFORM-owned settings — today the external-learner toggle —
// and a slug-availability check for real-time form validation, plus the one operation here
// that is not CRUD: handing over a tenant's pääkäyttäjyys (US-E9.4). Mirrors the backend
// `PlatformTenantEndpoints` surface under `/api/platform/tenants` — the one admin
// surface that legitimately spans tenants, gated on the platform-admin role
// (PLATFORM_ADMIN) server-side, so a 403 surfaces as an ApiError here.
//
// Nothing here is hand-declared any more. Every operation declares its success body, so both response
// types below come straight from the generated contract (F-2): the list 200 is `TenantDto[]`, create 201
// and the get/update/suspend/resume 200s are one `TenantDto`, and the slug probe's 200 is
// `TenantSlugAvailabilityDto`. No field of either is restated — neither carries an `int32` or an enum
// this console narrows.

import type { components } from "@clms/contracts";
import { apiFetch } from "@web/lib/api";

/**
 * A tenant (isolation root) as seen by the platform-admin console (TenantDto).
 *
 * No `name` and no `brandLogoUrl` since ADR-0014 §7.3: both moved to the tenant's OWN settings table,
 * which is tenant-owned — so this console structurally cannot read them, and the customer directory
 * identifies customers by `slug`. The slug addresses the tenant, is immutable and is derived from the
 * organisation's name at creation, so it reads as the organisation; a platform-side copy of the name
 * would go stale the first time a customer renamed itself.
 *
 * The rest of the fields, whose names do not carry their reasoning:
 *
 * `classIiiMaster` is ADR-0014 §3 — which system masters this tenant's class-III fields (a person's
 * org-unit placement, a unit's manager). A PLATFORM-owned setting (§7.3), which is why it is on this
 * console and on no tenant-facing surface: a tenant must not be able to declare itself the master of
 * fields its own HR system owns. One of the codes in `admin/classIiiMaster.ts`.
 *
 * `provisioningComplete` is ADR-0014 §7.1 / §12 C-5 — whether every provisioning step that can be done
 * today has been. This is why the DIRECTORY can show an unfinished onboarding without opening anything:
 * the requirement is that an incomplete provisioning is visible as state, and the per-tenant status read
 * (`provisioningApi`) is audited, so reading it for every row would file an audit entry per tenant nobody
 * looked at. It discloses no customer data — whether Verso finished onboarding a customer is the
 * platform's own fact, which is why this unaudited list may carry it while the tenant's own NAME cannot
 * (§7.3).
 *
 * `suspendedAt` is ADR-0014 §7.4 (decision f3ca7f9e) — when this tenant was FROZEN, or null while it is
 * live. A frozen tenant refuses every door to its own people — its administrators exactly as its
 * learners — and its background sweeps stand still, while nothing is deleted: the rows, the audit chain
 * and the keys are untouched, and `resumeTenant` opens it again. The tenant's LIFECYCLE is platform-owned
 * for the same reason the rest of the type is (§7.3): a customer cannot freeze or thaw itself. The
 * directory carries it so an operator can see which customers are shut without opening each one.
 * `suspensionReason` is why it was frozen, as the operator wrote it — recorded with the freeze, null
 * while the tenant is live.
 *
 * `entitlements` is ADR-0014 §7.1 step 3 — which applications the customer has bought — and it is on the
 * DIRECTORY for the reason `suspendedAt` is (task aa0c3eb7): since `ApplicationEntitlementGate` a customer
 * tenant without the application it is served on is refused every request it makes, so it is as shut as a
 * frozen one and the list was the one place that could not say so. What the backend may NOT send is a
 * "closed" verdict — deciding who is admitted belongs to that gate and to nothing else in the backend
 * (`ApplicationEntitlementGateGuardTests`) — so the list carries step 3's ANSWER and this console reads the
 * door's rule against it in `./tenantAccess`, which is where it keeps that rule.
 *
 * `entitlementsRecordedAt` is when step 3 last recorded an answer, or null when none is on record. It is
 * the fact `entitlements` cannot carry: an empty set is both "this onboarding never reached step 3" and
 * "these applications were taken away", which are a half-provisioned tenant and an ended contract — and
 * until this field the directory showed them as the same row.
 */
export type Tenant = components["schemas"]["TenantDto"];

/** Result of the slug-availability probe: the normalized slug + whether it is free & well-formed. */
export type SlugAvailability = components["schemas"]["TenantSlugAvailabilityDto"];

// Request DTOs come straight from the generated contract (single source of truth).
export type CreateTenantRequest = components["schemas"]["CreateTenantRequest"];
export type UpdateTenantRequest = components["schemas"]["UpdateTenantRequest"];
export type SuspendTenantRequest = components["schemas"]["SuspendTenantRequest"];

/** List all tenants (platform-admin), newest activity first as returned by the API. */
export function listTenants(signal?: AbortSignal): Promise<Tenant[]> {
  return apiFetch<Tenant[]>("/api/platform/tenants", { signal });
}

/**
 * Read one tenant — what the tenant's own page (`/tenants/:tenantId`) is built on.
 *
 * 404 for a tenant that does not exist AND for the operator's own tenant, which is not a customer
 * (ADR-0014 §12 A-2): the page renders that as a not-found, not as an error.
 *
 * This read IS audited, like `…/provisioning` and `…/principal-candidates`: it writes a `TENANT_ACCESSED`
 * row carrying `{"viewed":"tenant_profile"}` (US-E10.1 / FR-Q.35), which is why the backend takes the
 * caller's id for it. It is the only read on this service that discloses a NAMED tenant — the LIST above
 * is the platform's own customer directory and the slug check answers a yes/no about a string the caller
 * already holds, so those two are the unaudited ones (`TenantAdminService.GetTenantAsync`). A MISS writes
 * nothing: there was no tenant to access, and that covers the operator's own 404 above.
 *
 * Which makes this a per-PAGE read and never a per-ROW one. The tenant's page calls it once on load, and
 * that open is the operator's explicit choice of that customer, so the row records a crossing that really
 * happened. Reading it for every row of the directory would file a row for every tenant nobody looked at —
 * the directory has `listTenants`, whose fields are chosen so it does not need this (see
 * `provisioningComplete` above). The FR-Q.35 guard in `platform-tenant.realstack.spec.ts` holds that.
 */
export function getTenant(id: string, signal?: AbortSignal): Promise<Tenant> {
  return apiFetch<Tenant>(`/api/platform/tenants/${encodeURIComponent(id)}`, { signal });
}

/**
 * Check whether a tenant slug is well-formed and free (for inline form validation). An empty slug
 * responds 422; otherwise `{ slug, available }` with the server-normalized (lowercased) slug.
 */
export function checkSlug(slug: string, signal?: AbortSignal): Promise<SlugAvailability> {
  return apiFetch<SlugAvailability>(
    `/api/platform/tenants/slug-available?slug=${encodeURIComponent(slug)}`,
    { signal },
  );
}

/**
 * Create an isolated tenant — ADR-0014 §7.1 step 1. A taken slug responds 409 (problem extension
 * `field: "slug"`); a malformed slug / missing field responds 422 — both surface as an ApiError the
 * form recovers from, and no tenant is created (no partial state).
 *
 * It takes no name and no logo: the platform cannot write the tenant's settings row at all, so the new
 * tenant displays as its slug until its pääkäyttäjä names it on their own settings page.
 */
export function createTenant(request: CreateTenantRequest, signal?: AbortSignal): Promise<Tenant> {
  return apiFetch<Tenant>("/api/platform/tenants", { method: "POST", body: request, signal });
}

/**
 * Update a tenant's PLATFORM-owned settings. The slug is immutable — it addresses the tenant — and the
 * name and the logo are the tenant's own (ADR-0014 §7.3), so neither is in the request type.
 *
 * `classIiiMaster` is the one field whose omission does NOT reset it: leave it out and the tenant keeps its
 * master, because un-masking a tenant re-opens every class-III field to hand-editing (ADR-0014 §3) and that
 * has to be something a caller asked for. `allowsExternalLearners` is not like that — an omitted toggle
 * closes it. The console's own form sends both, so the distinction only matters to a caller that does not.
 */
export function updateTenant(
  id: string,
  request: UpdateTenantRequest,
  signal?: AbortSignal,
): Promise<Tenant> {
  return apiFetch<Tenant>(`/api/platform/tenants/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: request,
    signal,
  });
}

// ── task e03c428d · "Muunna asiakastenantiksi" ────────────────────────────────────────────────────

/** What one conversion removed and how completely (TenantDemoConversionDto). */
export type TenantDemoConversion = components["schemas"]["TenantDemoConversionDto"];

/** One reason a conversion refused — the same codes the tenant's own card localises. */
export type DemoDataReason = components["schemas"]["DemoDataReasonDto"];

/** One row a reason names, so the operator can say WHICH questionnaire or unit has to move. */
export type DemoDataBlockerTarget = components["schemas"]["DemoDataBlockerTargetDto"];

/**
 * `TenantDto.exampleDataset` — whether a DEMO tenant currently holds Verso's example dataset. Null for
 * every tenant that is not a demo tenant (mirrors the backend's `TenantExampleDatasetStates`).
 */
export const EXAMPLE_DATASET = {
  none: "none",
  present: "present",
  running: "running",
} as const;

/** `TenantDemoConversion.outcome` (mirrors `TenantDemoConversionOutcomes`). */
export const CONVERSION_OUTCOME = {
  /** Done, and every Transit key of the deleted people is confirmed gone. */
  converted: "converted",
  /**
   * The rows are gone and the mark is off — the conversion IS in effect — and `keysOutstanding` keys could
   * not be confirmed removed. Never reported as done (ADR-0013); the remedy is to call the same route
   * again, which by then retries nothing but the shred.
   */
  incomplete: "incomplete",
  /** The tenant was already a customer tenant, so only the shred was retried — and it completed. */
  shredRetried: "shred_retried",
} as const;

/**
 * The problem-details `error` codes this operation and the demo checkbox answer (mirrors
 * `TenantDemoConversionErrors`).
 *
 * `blocked` and `runInProgress` share the 409 deliberately: the status is the same fact — the state refused
 * a well-formed request — and what differs is the remedy, which is exactly what a code is for. A run in
 * progress is fixed by waiting; a blocker is fixed by somebody moving a questionnaire or a person, and the
 * console cannot offer that without the list in `blockers`.
 *
 * `datasetPresent` arrives from a different route — `PUT /{id}` — when the operator unticks *Demotenantti*
 * over a dataset that is still there. It is here beside the others because it is the same decision: the two
 * halves are one act, and this code is what sends the operator to the button that performs it.
 */
export const CONVERSION_ERROR = {
  runInProgress: "demo_data_run_in_progress",
  blocked: "demo_conversion_blocked",
  datasetPresent: "demo_dataset_present",
  partlyAudited: "demo_conversion_partly_audited",
} as const;

/**
 * Turn a demo tenant into a customer tenant: remove the example dataset and clear the demo mark, as ONE
 * all-or-nothing operation (owner decision 1.11.2026).
 *
 * It runs the same reset the tenant's own pääkäyttäjä runs — only the two demo measurements, the 306
 * synthetic people and the `(demo)` units — so everything the tenant itself made during the demo stays.
 *
 * Every failure is an {@link ApiError} the caller turns into its own message, and in all of them except the
 * last NOTHING changed:
 *
 *   * 409 `demo_conversion_blocked` — something outside the dataset names a row the reset would delete.
 *     The problem's `blockers` extension says what, by code and count and (where there is a row to point
 *     at) its type, id and name. Read it with `conversionBlockers` in `./tenantDemoConversion`.
 *   * 409 `demo_data_run_in_progress` — a creation or a reset is already running for that tenant.
 *   * 404 — no such tenant, or the operator's own tenant, which is not a customer.
 *   * 500 `demo_conversion_partly_audited` — the conversion IS committed and one of the two rows the
 *     PLATFORM's chain is owed did not land. It must NOT be retried.
 *
 * It is deliberately NOT idempotent in the weak sense: calling it again on a converted tenant is the SHRED
 * RETRY (`shred_retried`), which is the only way to finish an `incomplete` conversion — by then the demo
 * mark is off, so the tenant's own card is gone and this route is the one door left.
 */
export function convertTenantFromDemo(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantDemoConversion> {
  return apiFetch<TenantDemoConversion>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/convert-from-demo`,
    { method: "POST", signal },
  );
}

// ── ADR-0023 · deleting a demo tenant (owner decision 1.11.2026) ──────────────────────────────────

/** What one deletion — or one retry of it — did and what it still owes (TenantDeletionDto). */
export type TenantDeletion = components["schemas"]["TenantDeletionDto"];

/** One store with work left on it: which store, how many obligations, and the last refusal. */
export type TenantDeletionOutstanding = components["schemas"]["TenantDeletionOutstandingDto"];

/** One deletion as the LIST reports it — the id and slug of a tenant that no longer exists. */
export type TenantDeletionSummary = components["schemas"]["TenantDeletionSummaryDto"];

export type DeleteTenantRequest = components["schemas"]["DeleteTenantRequest"];

/**
 * `TenantDeletion.status` / `TenantDeletionSummary.status` (mirrors `TenantDeletionStatuses`).
 *
 * `done` is `count(*) = 0` over the obligation ledger and NOT "every step was attempted" — ADR-0013's rule
 * that an unconfirmed crypto-shred is never reported as finished, applied to the one operation where
 * reporting it as finished would be convenient.
 */
export const DELETION_STATUS = {
  done: "done",
  /**
   * At least one obligation is outstanding. The rows and the tenant ARE gone — this says something about
   * EXTERNAL stores only — and the normal successful path lands here for about one provisioner pass,
   * because the realm IdP alias is the provisioning component's to remove (ADR-0023 §6.2a).
   */
  incomplete: "incomplete",
} as const;

/**
 * Delete a frozen demo tenant, or retry what a previous deletion of it still owes (ADR-0023).
 *
 * IRREVERSIBLE, and the confirmation is part of the request: `slug` is what the operator typed, compared
 * server-side against the tenant's slug — or, once the tenant row is gone, against the ledger's
 * `tenant_slug` and then the `TENANT_DELETED` audit row's. It is asked for on EVERY press, retries
 * included (§7 item 3), so this function has no "I already confirmed" shape.
 *
 * A 200 does NOT mean the cleanup finished: read {@link TenantDeletion.status}. `incomplete` names the
 * stores still owed in `outstanding`, and for `keycloak_federation` the remedy is to WAIT — the
 * request-path API has no call path to the realm at all (ADR-0014 §7.2).
 *
 * CALLING IT AGAINST A TENANT THAT NO LONGER EXISTS IS THE RETRY, not an error: with obligations left it
 * works them, and with none left it answers `done` with an empty `outstanding` and changes nothing. Which
 * is why a 404 here means something narrow — this console has no record of the id at all — and must not be
 * rendered as a failed cleanup.
 *
 * Every refusal is an {@link ApiError} whose problem `error` carries the code, because the status cannot
 * tell the four 409s apart and each has a different remedy: 422 `slug_mismatch`; 409 `tenant_not_demo`,
 * `tenant_not_suspended`, `tenant_is_platform_operator`, `demo_data_run_in_progress` and
 * `tenant_refused_by_database` (the row changed between the service's checks and the purge's lock). In all
 * of them NOTHING was deleted.
 */
export function deleteTenant(
  id: string,
  request: DeleteTenantRequest,
  signal?: AbortSignal,
): Promise<TenantDeletion> {
  return apiFetch<TenantDeletion>(`/api/platform/tenants/${encodeURIComponent(id)}`, {
    method: "DELETE",
    body: request,
    signal,
  });
}

/**
 * Every demo-tenant deletion this console can still see: the ones with obligations outstanding (no time
 * limit) and the recently completed ones, newest first (ADR-0023 §6.3a).
 *
 * BOTH halves, which is why it is read on the tenant DIRECTORY and not only on a page of a tenant that no
 * longer exists. The ADR adds no background worker that retries cleanup, and that is defensible only
 * because an outstanding obligation is visible without being looked for; and because `done` is computed
 * from the ledger count at read time, a deletion the provisioning component finished while nobody was
 * watching turns up here as `done` rather than vanishing.
 *
 * It appends no audit row and names no person — a deleted tenant's slug is the platform's own fact — so,
 * unlike the per-tenant reads, it may run on a list nobody has chosen a tenant from.
 */
export function listTenantDeletions(signal?: AbortSignal): Promise<TenantDeletionSummary[]> {
  return apiFetch<TenantDeletionSummary[]>("/api/platform/tenants/deletions", { signal });
}

// ── ADR-0014 §7.4 · the reversible freeze (decision f3ca7f9e) ─────────────────────────────────────

/**
 * Freeze a tenant: no sign-in on any door, no capability token, no background work for it.
 *
 * Reversible by {@link resumeTenant}, and nothing is deleted — the rows, the audit chain and the
 * encryption keys are untouched. Idempotent: freezing an already frozen tenant answers 200 and changes
 * neither `suspendedAt` nor the recorded reason, so a double-click cannot rewrite why a customer was shut.
 *
 * 404 for an unknown tenant and for the Verso operator tenant, which can never be frozen; 422 when the
 * reason is longer than 500 characters. The reason is OPTIONAL to the backend — the console requires it
 * anyway, because "why" is the one thing the audit row cannot reconstruct afterwards.
 */
export function suspendTenant(
  id: string,
  request: SuspendTenantRequest,
  signal?: AbortSignal,
): Promise<Tenant> {
  return apiFetch<Tenant>(`/api/platform/tenants/${encodeURIComponent(id)}/suspend`, {
    method: "POST",
    body: request,
    signal,
  });
}

/**
 * Thaw a frozen tenant: every door opens again and the background sweeps resume. Nothing has to be
 * restored, because nothing was removed. Idempotent — resuming a live tenant answers 200 and writes
 * nothing. 404 for an unknown tenant and for the Verso operator tenant.
 */
export function resumeTenant(id: string, signal?: AbortSignal): Promise<Tenant> {
  return apiFetch<Tenant>(`/api/platform/tenants/${encodeURIComponent(id)}/resume`, {
    method: "POST",
    signal,
  });
}

// ── US-E9.4 · handing over tenant-pääkäyttäjyys ───────────────────────────────────────────────────

/** A person in a named tenant as the handover surface sees them (TenantPrincipalCandidateDto). */
export type TenantPrincipalCandidate = components["schemas"]["TenantPrincipalCandidateDto"];
/** A completed handover: the realm role that moved, and both parties in their NEW state. */
export type TenantPrincipalTransfer = components["schemas"]["TenantPrincipalTransferDto"];
/** Both sides are named on purpose — the operation is "this seat moves from A to B". */
export type TransferTenantPrincipalRequest =
  components["schemas"]["TransferTenantPrincipalRequest"];

/** The `status` value a candidate must carry to be able to receive the seat (mirrors the service). */
export const ACTIVE_STATUS = "active";

/**
 * The people a handover can name in one tenant, and who holds pääkäyttäjyys today. 404 when there is no
 * such tenant.
 *
 * Reading this is itself an audited cross-tenant access (`TENANT_ACCESSED`, `{"viewed":"tenant_principals"}`
 * — FR-Q.35), so it is called when the operator OPENS the handover, never to decorate the tenant list:
 * a page that read it in the background would write an audit row for a tenant nobody looked at.
 */
export function listPrincipalCandidates(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantPrincipalCandidate[]> {
  return apiFetch<TenantPrincipalCandidate[]>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/principal-candidates`,
    { signal },
  );
}

/**
 * Hand tenant-pääkäyttäjyys from one named person to another in one audited operation (US-E9.4).
 *
 * Every failure is an {@link ApiError} the caller turns into its own message, and in all of them the seat
 * stays where it was: 404 (tenant or person unknown), 409 (the named outgoing holder does not hold it),
 * 403 (the operator named themselves), 422 (same person twice / inactive / shredded / no IdP identity),
 * 502 (the identity provider did not answer — nothing was transferred). The one exception is a 500, where
 * the role may have moved without its tenant-side audit row — see the caller's message for it.
 */
export function transferTenantPrincipal(
  tenantId: string,
  request: TransferTenantPrincipalRequest,
  signal?: AbortSignal,
): Promise<TenantPrincipalTransfer> {
  return apiFetch<TenantPrincipalTransfer>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/principal-transfer`,
    { method: "POST", body: request, signal },
  );
}

// ── ADR-0018 §7 · the grant surface for §3.3's tenant roles ───────────────────────────────────────

/** One holder of a grantable tenant role, and WHICH carrier gives it to them (TenantRoleHolderDto). */
export type TenantRoleHolder = components["schemas"]["TenantRoleHolderDto"];
/** A completed grant or removal: the holder, and whether an account had to be made for them. */
export type TenantRoleGrant = components["schemas"]["TenantRoleGrantDto"];
export type GrantTenantRoleRequest = components["schemas"]["GrantTenantRoleRequest"];
export type RevokeTenantRoleRequest = components["schemas"]["RevokeTenantRoleRequest"];

/**
 * The role codes this surface grants (ADR-0018 §3.3), in the order the form offers them.
 *
 * Restated here rather than read from the contract, which is the one thing on this service that is: the
 * backend's set is `TenantMemberships.GrantableRoles`, a C# `HashSet` that reaches no OpenAPI schema at
 * all — the request's `role` is a plain string, because a server that answered 422 only for a value its
 * own enum forbade could not name the RULE that refused it (which is the whole point of §3.3's refusal).
 * So the console's picker has to carry the list, and the 422 is what keeps the two honest: a code this
 * array gained and the backend did not is refused by name the first time anybody tries it.
 *
 * THE ORDER IS PART OF THE CONTRACT WITH THE FORM and not alphabetical. `grantEachInTurn` sends the ticked
 * codes in this order, and the FIRST call is the one that may create the realm account and send Keycloak's
 * activation mail — so `CONTENT_MANAGER` was APPENDED when §3.3 widened (27.10.2026) rather than inserted,
 * which leaves which code creates the account exactly where it was.
 *
 * `HRM_HENKILOSTOASIANTUNTIJA` (ADR-0021 §3.4, 30.10.2026) is APPENDED for that same reason: it is the most
 * privileged code in the set — ADR-0020 §4 puts the henkilötunnus and the salary behind it — and appending
 * it leaves the account-creating first call where it already was.
 *
 * `ADMIN` (ADR-0026 §3, 3.11.2026, task `f64f2b64`) is the fifth, APPENDED for the same reason again. It is
 * the product owner's decision of 3.11.2026 — an operator may grant `ADMIN` in any tenant, including to
 * themselves — which reverses ADR-0018 §3.3's and ADR-0021 §3.4's refusal of this code.
 *
 * NOTHING HOLDS THIS ARRAY AGAINST THE C# SET, in either direction (ADR-0026 §11), so both sides are
 * hand-edited in one diff and the divergence is visible only at runtime and asymmetrically: a code added in
 * C# alone renders no checkbox and says nothing, while a code added HERE alone is refused per grant with a
 * 422 `role_not_grantable`. What `platform-i18n.test.mjs` does catch is this array gaining a code with no
 * label, which is a different mistake and the one that would otherwise ship (`:164-171`). §12 records
 * pinning the two sets together as undecided — it is a new check and needs the owner's yes, and the gap
 * predates the decision.
 *
 * What appending does NOT do: it does not stop HRM creating an account. `TenantRolesSection` FILTERS this
 * array to the ticked codes before `grantEachInTurn` sends it, so a submission that ticks HRM ALONE makes
 * HRM the first and only call — and that call creates the realm account if the address has none. The
 * position only decides the order WITHIN a multi-code submission; the address is what decides whether an
 * account is created, and nothing in this array can guard a mistyped one.
 */
export const GRANTABLE_TENANT_ROLES = [
  "MJ_VASTUUKONSULTTI",
  "HR",
  "CONTENT_MANAGER",
  "HRM_HENKILOSTOASIANTUNTIJA",
  "ADMIN",
] as const;

/** The carrier values `TenantRoleHolder.carrier` can take (mirrors `TenantRoleCarriers`). */
export const CARRIER_MEMBERSHIP = "membership";
export const CARRIER_CLIENT_ROLE = "client_role";

/** `TenantRoleGrant.account` when the grant CREATED the realm account (mirrors `TenantRoleAccountOutcomes`). */
export const ACCOUNT_CREATED = "created";
/** `TenantRoleGrant.mail` when Keycloak would not send the activation link (mirrors `FirstAdminMailOutcomes`). */
export const MAIL_SEND_FAILED = "send_failed";

/**
 * The problem-details `error` of the grant's and the removal's DECLARED 500 (mirrors
 * `TenantRoleErrors.ChangeInEffect` and `TenantRoleErrors.ChangeNotInEffect`).
 *
 * The route answers one 500 with one title and the `error` says which of two OPPOSITE things happened.
 * The grant's two 422s needed the same arrangement until ADR-0021 §7 removed §9's twenty-tenant ceiling
 * and its `membership_limit_reached`; this is now the only place on the surface that needs it.
 * `change_in_effect` is the ending this surface has always had: the
 * authority is a Keycloak write nothing can take back, so only the customer's audit row is missing and a
 * retry is the wrong act. `change_not_in_effect` is ADR-0021 §8.1's new one: a cross-tenant membership is a
 * row written INERT and activated only once both audit rows are in, so a chain that could not be written
 * means nobody's access changed — and the retry is exactly what finishes it.
 */
export const CHANGE_IN_EFFECT = "change_in_effect";
export const CHANGE_NOT_IN_EFFECT = "change_not_in_effect";

/**
 * The problem-details `error` of ADR-0026 §4's floor (mirrors `TenantRoleErrors.LastAdministrator`): the
 * REMOVAL was refused because it would have left the tenant with no `ADMIN`.
 *
 * It needs its own code for the reason {@link CHANGE_IN_EFFECT} does, one status lower: this surface puts
 * TWO refusals on 409 and they ask the operator for OPPOSITE things. The other one — an account whose
 * `tenant_id` attribute names no tenant — is fixed in the Keycloak console and by nobody on this screen;
 * this one is fixed here, by granting somebody else `ADMIN` first and then removing this one. The two reach
 * one status map (`PlatformTenantEndpoints` maps the grant's and the removal's statuses in one switch), so a
 * caller branching on the number alone could only print one of the two remedies for both.
 *
 * It is the REMOVAL's code and the grant never answers it. Nothing was removed, and the role is still in
 * effect — which is the half that makes the floor a protection rather than a warning.
 */
export const LAST_ADMINISTRATOR = "last_administrator";

/**
 * Who holds one of {@link GRANTABLE_TENANT_ROLES} in this tenant today, from BOTH carriers — the memberships
 * (people whose accounts are bound to another tenant, so they have no user row here at all) and the
 * client roles among the tenant's own people.
 *
 * Reading it is an audited cross-tenant access (`TENANT_ACCESSED`, `{"viewed":"tenant_role_holders"}` —
 * FR-Q.35), so it is called when the operator OPENS the section, exactly like the principal candidates
 * above and for the same reason: choosing a tenant is not choosing to look at who works there.
 *
 * A 502 means the identity provider did not answer and the list is NOT empty — it is unknown. The caller
 * must say so rather than rendering nobody (ADR-0016 §4.7 point 4).
 */
export function listTenantRoleHolders(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantRoleHolder[]> {
  return apiFetch<TenantRoleHolder[]>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/roles`,
    { signal },
  );
}

/**
 * Grant a role in this tenant to an address. The server picks the write from a fact already in the realm
 * (ADR-0018 §7): a membership row for an account bound to another tenant (ADR-0021 §8), the client role for
 * one bound here, a new account plus Keycloak's activation mail for an address the realm has never heard of.
 *
 * Idempotent, so a repeated submit re-asserts the grant rather than doubling it. It is NOT bounded by the
 * number of tenants the account already reaches: ADR-0021 §7 removed ADR-0018 §9's ceiling and its 422,
 * because the sign-in token no longer grows with the membership count. Every failure is an
 * {@link ApiError} with its own message: 422 for a code §3.3 does not grant this way (`error`
 * `role_not_grantable`, and the detail names the rule), 409 for an unreadable `tenant_id` attribute, 502
 * when the identity provider did not complete — in all of which NOTHING was granted. The 500 is the one
 * that needs its `error` read:
 * {@link CHANGE_IN_EFFECT} means the grant landed and only the customer's audit row is missing, so the
 * caller's message must not invite a retry, and {@link CHANGE_NOT_IN_EFFECT} means the opposite — nobody's
 * access changed and the retry is the remedy.
 */
export function grantTenantRole(
  tenantId: string,
  request: GrantTenantRoleRequest,
  signal?: AbortSignal,
): Promise<TenantRoleGrant> {
  return apiFetch<TenantRoleGrant>(`/api/platform/tenants/${encodeURIComponent(tenantId)}/roles`, {
    method: "POST",
    body: request,
    signal,
  });
}

/**
 * Remove one holder's role. It names the account's SUBJECT — the pseudonymous id the holder list already
 * carries — and not an address, so nobody's email lands in a request line, and a stale list cannot resolve
 * to a different account than the one on screen.
 *
 * A POST and not a DELETE because the removal has to name who, and the alternatives are the request line
 * (where the id lands in every access log) or a DELETE body (which not every client sends).
 *
 * It removes the membership or the client role and nothing else: not the account, not its `tenant_id`, not
 * the person's rows. 404 when they hold the role by neither carrier here — reload the list.
 *
 * ITS TWO ANSWERS THAT THE GRANT DOES NOT HAVE:
 *
 *   * 409 {@link LAST_ADMINISTRATOR} — ADR-0026 §4's floor, and the role is still in effect. Removing this
 *     `ADMIN` would leave the customer with nobody who can administer their organisation, so it is refused
 *     before anything is written; the remedy is to grant `ADMIN` to somebody else first.
 *   * 502 — the identity provider did not answer, and this one does NOT say what became of the role. The
 *     backend gives the same status and no code to three endings: a read that would not say how the account
 *     holds the role, §4c's INDETERMINATE count, and the realm write itself not completing. Only the first
 *     two refuse before writing. The third is a role-mapping DELETE the product had already SENT whose
 *     answer was lost or timed out, so the role may be gone — which is why the server's own detail for it
 *     says it "may still be held" rather than that it is. A caller may therefore render neither "the
 *     removal is in effect" (that sentence belongs to the 500 with {@link CHANGE_IN_EFFECT} and to nothing
 *     else) nor "nothing was removed", and the retry it asks for is safe: the realm write is idempotent
 *     both ways.
 */
export function revokeTenantRole(
  tenantId: string,
  request: RevokeTenantRoleRequest,
  signal?: AbortSignal,
): Promise<TenantRoleGrant> {
  return apiFetch<TenantRoleGrant>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/roles/revoke`,
    { method: "POST", body: request, signal },
  );
}
